import { inject, Injectable } from '@angular/core';
import { AddressV2, AddressV2Create, AddressV2Update } from '@zayan-erp/home-building';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, Observable, of, shareReplay, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  #http = inject(ApiCallingV2Service);
  #toast = inject(ToastrService);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  // Cache addresses to avoid unnecessary API calls
  #addressesCache$ = new BehaviorSubject<AddressV2[]>([]);
  #addressesLoaded = false;

  getAddresses(): Observable<AddressV2[]> {
    if (this.#addressesLoaded) {
      return this.#addressesCache$.asObservable();
    }

    // Load addresses from API
    return this.readAddresses().pipe(
      tap(addresses => {
        this.#addressesCache$.next(addresses);
        this.#addressesLoaded = true;
      }),
      catchError(error => {
        console.error('Error loading addresses:', error);
        return of([]);
      }),
      shareReplay(1)
    );
  }

  createAddress(address: AddressV2Create): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/Address`,
      { showLoader: true, showMessage: true, message: 'Address created successfully' },
      address
    );
  }

  readAddresses(): Observable<AddressV2[]> {
    return this.#http.getData(`${this.baseUrl}/Address`, { showLoader: true, showMessage: false });
  }

  filterAddresses(addressType?: string, cityId?: string, search?: string): Observable<AddressV2[]> {
    let params = new URLSearchParams();
    if (addressType) params.append('addressType', addressType);
    if (cityId) params.append('cityId', cityId);
    if (search) params.append('search', search);

    return this.#http.getData(`${this.baseUrl}/Address/filter?${params.toString()}`, {
      showLoader: true,
      showMessage: false
    });
  }

  updateAddress(address: AddressV2Update): Observable<unknown> {
    return this.#http.putData(`${this.baseUrl}/Address/${address.id}`, address, {
      showLoader: true,
      showMessage: true,
      message: 'Address updated successfully'
    });
  }

  deleteAddress(ids: string[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/Address/bulk-delete`,
      { showLoader: true, showMessage: true, message: 'Address deleted successfully' },
      ids
    );
  }

  upsertAddress(address: AddressV2Create[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/Address/bulk-upsert`,
      { showLoader: true, showMessage: true, message: 'Projects added successfully' },
      address
    );
  }

  mapExternalIdsToAddressIds<T extends object>(
    items: T[],
    externalIdField: keyof T,
    targetIdField: keyof T
  ): Observable<T[]> {
    // Note: AddressV2 doesn't have externalAId field
    // This method may need to be updated or removed based on component requirements
    // For now, returning items as-is since AddressV2 doesn't support external ID mapping
    console.warn('mapExternalIdsToAddressIds: AddressV2 does not support externalAId field');
    return of(items);
  }

  isValidAddressId(addressId: string | null, addresses: AddressV2[]): boolean {
    if (!addressId) return false;
    return addresses.some(address => address.id === addressId);
  }

  // private showSuccessMessage(body: any, message?: string) {
  //   const msg = body.message || message || 'Success';
  //   if (msg) this.#toast.success(msg);
  // }

  // private handleError(error: any) {
  //   const errorMessage = error.message || 'An error occurred';
  //   this.#toast.error(errorMessage);
  //   return EMPTY;
  // }
}
